import { z } from "zod";
import { Finite, IsoDate, Symbol_ } from "./primitives";
import { SleeveId } from "./scoring";

export const BacktestPeriod = z.object({
  /** Decision date. Only data available on/before this date was used. */
  date: IsoDate,
  picks: z.array(Symbol_),
  /** Equal-weighted forward return of the picks over the hold window. */
  portfolioReturn: Finite,
  benchmarkReturn: Finite,
  /** Equal-weight return of the whole eligible universe, the harder benchmark. */
  universeReturn: Finite,
  excessVsBenchmark: Finite,
  excessVsUniverse: Finite,
});
export type BacktestPeriod = z.infer<typeof BacktestPeriod>;

export const BacktestSummary = z.object({
  periods: z.number().int().nonnegative(),
  cumulativeExcessVsBenchmark: Finite,
  cumulativeExcessVsUniverse: Finite,
  annualizedExcess: Finite,
  /** Share of periods where picks beat the benchmark, 0..1. */
  hitRate: Finite.min(0).max(1),
  avgWin: Finite,
  avgLoss: Finite,
  maxDrawdown: Finite,
  /** Mean fraction of the book replaced each rebalance, 0..1. */
  turnover: Finite.min(0).max(1),
});
export type BacktestSummary = z.infer<typeof BacktestSummary>;

/**
 * Per-sleeve attribution.
 *
 * The point of separating this out is that price-based sleeves (momentum,
 * trend quality, risk) are genuinely point-in-time, while fundamentals-based
 * ones (value, quality) are reconstructed and therefore biased. If the whole
 * apparent edge lives in the fundamental sleeves, that is evidence of
 * lookahead rather than skill, and this table is how we would see it.
 */
export const SleeveAttribution = z.object({
  sleeve: SleeveId,
  /** Excess return attributable to this sleeve. */
  contribution: Finite,
  /** True when this sleeve's inputs are genuinely point-in-time. */
  pointInTime: z.boolean(),
});
export type SleeveAttribution = z.infer<typeof SleeveAttribution>;

export const BacktestResult = z.object({
  generatedAt: z.string(),
  from: IsoDate,
  to: IsoDate,
  holdMonths: z.number().int().positive(),
  topN: z.number().int().positive(),
  benchmark: Symbol_,
  summary: BacktestSummary,
  periods: z.array(BacktestPeriod),
  attribution: z.array(SleeveAttribution),
  /**
   * Known biases and limitations, rendered verbatim in the UI.
   *
   * This field is not optional and not decorative. A backtest without its
   * caveats attached is a marketing number, and the whole reason this harness
   * exists is to avoid producing one.
   */
  caveats: z.array(z.string()).min(1),
});
export type BacktestResult = z.infer<typeof BacktestResult>;
